import {
    Badge,
    FluentProvider,
    makeStyles,
    Menu,
    MenuButtonProps,
    MenuDivider,
    MenuGroup,
    MenuGroupHeader,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    SplitButton,
} from '@fluentui/react-components';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { FeatureFlagName } from '../../constants/features';
import { useCurrentFluent2Theme } from '../../hooks/styling';
import { useHorizontalStackStyles } from '../../themes/styles/flexbox-styles';
import { isFeatureFlagEnabled } from '../../utilities/features';

interface PrimaryActionsButtonProps {
    primaryActions: JSX.Element[];
    headlessActions?: JSX.Element[];
    disabled?: boolean;
}

const messages = defineMessages({
    splitButtonAriaLabel: {
        id: 'PrimaryActionsButton_SplitButton_AriaLabel',
        defaultMessage: 'See more actions',
        description: 'Aria label for dropdown button in primary actions split button for dev box cards',
    },
});

/**
 * Styles
 */

const badgeStyle: React.CSSProperties = {
    marginLeft: '12px',
};

const useGroupHeaderStyles = makeStyles({
    root: {
        justifyContent: 'space-between',
        width: '100%',
    },
});

/**
 * End Styles
 */

export const PrimaryActionsButton: React.FC<PrimaryActionsButtonProps> = React.memo(
    (props: PrimaryActionsButtonProps) => {
        const { primaryActions, headlessActions, disabled } = props;

        // Intl hooks
        const { formatMessage } = useIntl();

        const theme = useCurrentFluent2Theme();

        // Style hooks
        const horizontalStackStyles = useHorizontalStackStyles();
        const groupHeaderStyles = useGroupHeaderStyles();

        // Memoized data
        const { ariaLabel, icon, onClick, children } = React.useMemo(() => primaryActions[0].props, [primaryActions]);

        const primaryActionButtonProps = {
            onClick,
        };

        const menuButtonProps = {
            id: 'PrimaryActionsButton',
            ariaLabel: formatMessage(messages.splitButtonAriaLabel),
        };

        // If VSCode feature is enabled and headless actions has at least 1 item, we know to show these items with sub-headings.
        // If tunnel is enabled, will have 2 items, if not, just 1.
        const showConnectWithVSCode =
            isFeatureFlagEnabled(FeatureFlagName.EnableConnectWithVSCode) &&
            headlessActions &&
            headlessActions.length >= 1;

        if (showConnectWithVSCode) {
            return (
                <FluentProvider theme={theme}>
                    <Menu positioning="below-end">
                        <MenuTrigger>
                            {(triggerProps: MenuButtonProps) => (
                                <SplitButton
                                    id="DefaultPrimaryButton"
                                    icon={icon}
                                    disabled={disabled}
                                    aria-label={ariaLabel}
                                    primaryActionButton={primaryActionButtonProps}
                                    menuButton={{ ...triggerProps, ...menuButtonProps }}
                                >
                                    {children}
                                </SplitButton>
                            )}
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuGroup>
                                    <MenuGroupHeader>
                                        <div className={groupHeaderStyles.root}>
                                            <FormattedMessage
                                                id="DevBoxCard_RemoteDesktopLabel_Text"
                                                defaultMessage="Remote Desktop"
                                                description='Text for "Remote desktop" subheading in dev box card split button'
                                            />
                                        </div>
                                    </MenuGroupHeader>

                                    {primaryActions}
                                </MenuGroup>
                                <MenuDivider />
                                <MenuGroup>
                                    <MenuGroupHeader>
                                        <div
                                            className={mergeClasses(horizontalStackStyles.root, groupHeaderStyles.root)}
                                        >
                                            <div className={horizontalStackStyles.item}>
                                                <FormattedMessage
                                                    id="DevBoxCard_HeadlessConnectionLabel_Text"
                                                    defaultMessage="Headless Connection"
                                                    description='Text for "Headless Connection" subheading in dev box card split button'
                                                />
                                            </div>
                                            <Badge
                                                style={badgeStyle}
                                                appearance="outline"
                                                color="informative"
                                                size="large"
                                            >
                                                <FormattedMessage
                                                    id="DevBoxCard_HeadlessConnectionPreview_Tag"
                                                    defaultMessage="Preview"
                                                    description="Tag used to indicate that a feature is in preview"
                                                />
                                            </Badge>
                                        </div>
                                    </MenuGroupHeader>
                                    {headlessActions}
                                </MenuGroup>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </FluentProvider>
            );
        }

        return (
            <FluentProvider theme={theme}>
                <Menu positioning="below-end">
                    <MenuTrigger>
                        {(triggerProps: MenuButtonProps) => (
                            <SplitButton
                                id="DefaultPrimaryButton"
                                icon={icon}
                                disabled={disabled}
                                aria-label={ariaLabel}
                                primaryActionButton={primaryActionButtonProps}
                                menuButton={{ ...triggerProps, ...menuButtonProps }}
                            >
                                {children}
                            </SplitButton>
                        )}
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>{primaryActions}</MenuList>
                    </MenuPopover>
                </Menu>
            </FluentProvider>
        );
    }
);

export default PrimaryActionsButton;
